import React, { useEffect, useMemo, useState, useRef } from "react";
import * as yup from "yup";
import { FaFolder, FaPlay } from "react-icons/fa";
import { Button, Modal, Radio, Table } from "antd";
import moment from 'moment';
import styles from "../../styles.module.scss";
import { Flex } from "@components/ui/Flex";
import { ShowWhenTrue } from "@helpers/showwhentrue";
import { Text } from "@components/ui/Text";
import { ModelRequestType, RootType, formTitle } from "./constant";
import { isEmpty } from "lodash";
import { format as formatSql } from "sql-formatter";
import HierarchyReqForm from "./Forms/ReqForm";
import HierarchySinkForm from "./Forms/SinkForm";
import HierarchyScheduleForm from "./Forms/ScheduleForm";
import { DataSourceHandler } from "@api/data-source-handler";
import { useSelector } from "react-redux";
import { RootState } from "@store/types";
import { errorAlert, successAlert } from "@components/toastify/notify-toast";
import { errorHandlerFor400 } from "@api/services/errorhandler";
import { TableMetaData } from "@pages/data-browser/treeviews/connections-treeview";
import { _canvasdagFormInitialValues, executionTimeFrequencyMultiplier } from "@pages/workflow-page/utils";
import { uuid } from "uuidv4";
import { ScheduleTiming, formatScheduleStartDate } from "@pages/workflow-page/modals/schedule-workflow";
import { Attribute } from "@pages/admin/asset_heirarchy/types";
import CustomMonacoEditor from "@components/monacoEditor";
import { InPageSpinner } from "@components/spinners/in-page-spinner";
import { WarningIcon } from "@assets/icons";

interface HierarchyViewProps {
  formValuesForEdit?: ModelRequestType | null;
  selectedElements: RootType;
  setFormValuesForEdit?: (formValues: ModelRequestType | null) => void;
  tableDataSources: any;
  columns: any[];
  isLoadingElements: boolean;
  showForm: boolean;
  activeStep: number;
  activeRecordsMetadata?: TableMetaData,
  selectedRowKeys: any[];
  pipoints: Attribute[]
  setActiveStep: (step: number) => void;
  setShowForm: (show: boolean) => void;
  setSelectedRowKeys: (keys: any[]) => void;
  setIsLoadingElements: (loading: boolean) => void;
  handleTableChange: (pagination: any, filters: any, sorter: any, extra: { action: string; currentDataSource: any; }) => any;
  handleGoToReqPage?: () => void;
  tagPosition: string;
  isProjectHierarchy?: boolean;
  setTagPosition: (position: string) => void;
  isHandleSelectRunning?: boolean;
  isRDBMSview?: boolean;
  isAspenIp21View?: boolean;
}

const formSchemaReqForm = yup.object().shape({
  requestName: yup
    .string()
    .required("This is a required field")
    .matches(/^[A-Za-z0-9_-]+$/, "Special Characters are not allowed except _ and -"),
  elementRootPath: yup.string(),
  requestType: yup.string().required("This is a required field"),
  elementPath: yup.string(),
  output_format: yup.string(),
  all_descendants: yup.boolean(),
  tags: yup.string().required("This field is required. Please select attributes on the previous page or from the options above."),
  start_time_str: yup.mixed().when('requestType', {
    is: (value) => value !== 'real_time',
    then: yup.mixed().required('This is a required field')
      .test("is-not-empty", "Start time cannot be empty", (value) => {
        return value !== null && value !== undefined && value !== "";
      })
      .test("is-past-date", "Start time must be in the past", (value) => {
        return value ? moment.utc(value).isBefore(moment.utc()) : false;
      }),
  }),
  end_time_str: yup.mixed().when('requestType', {
    is: (value) => value !== 'real_time',
    then: yup.mixed().required('This is a required field')
      .test("is-not-empty", "End time cannot be empty", (value) => {
        return value !== null && value !== undefined && value !== "";
      })
      .test("is-valid-end-time", "End time must be after start time", function (value) {
        const { start_time_str } = this.parent;
        return value && start_time_str ? moment.utc(value).isAfter(moment.utc(start_time_str)) : false;
      })
      .test("is-before-now", "End time must be before the current time", (value) => {
        return value ? moment.utc(value).isBefore(moment.utc()) : false;
      }),
  }),
  frequency: yup.string().when('requestType', {
    is: 'interpolated',
    then: yup.string().required('This is a required field'),
  }),
  frequencyType: yup.string().when('requestType', {
    is: 'interpolated',
    then: yup.string().required('This is a required field'),
  }),
  summary_types: yup.string().when('requestType', {
    is: 'summary',
    then: yup.string().required('This is a required field'),
  }),
  summary_interval_value: yup.string().matches(/^\d*$/, 'Only positive numbers are allowed'),
  page_size: yup.string().matches(/^\d*$/, 'Only positive numbers are allowed'),
  chunking_num_tags: yup.string().matches(/^\d*$/, 'Only positive numbers are allowed'),
  realTimeValues: yup.string().when('requestType', { is: 'real_time', then: yup.string().required('This is a required field') }),
  period_ms: yup.string().when('realTimeValues', { is: 'live', then: yup.string().required('This is a required field') }),
  refresh_period_ms: yup.string().when('realTimeValues', { is: 'live', then: yup.string().required('This is a required field') }),
});

const formSchemaSinkForm = yup.object().shape({
  sinkKind: yup
    .string()
    .required("This is a required field"),
  sinkName: yup
    .string()
    .required("This is a required field"),
  landingEnabled: yup.boolean(),
  databaseType: yup.string().when('landingEnabled', {
    is: true,
    then: yup.string(),
  }),
  tableName: yup.string().when('landingEnabled', {
    is: true,
    then: yup.string(),
  }),
});

const formSchemaReqFormRDBMS = yup.object().shape({
  requestName: yup
    .string()
    .required("This is a required field")
    .matches(/^[A-Za-z0-9_-]+$/, "Special Characters are not allowed except _ and -"),
  columns: yup.array().of(yup.string()),
});

const formSchemaReqFormIp21Aspen = yup.object().shape({
  requestName: yup
    .string()
    .required("This is a required field")
    .matches(/^[A-Za-z0-9_-]+$/, "Special Characters are not allowed except _ and -"),
  mode: yup
    .string()
    .required("This is a required field"),
  parameters: yup.array().when('mode', {
    is: 'recurring',
    then: yup.array().of(yup.object().shape({
      type: yup.string(),
      value: yup.string()
    })),
  }),
});

const extractPaths = (input: string) => {
  const parts = input.split("\\").filter(Boolean);
  if (parts.length === 0) {
    return { rootPath: "", elementPath: "" };
  }
  const rootPath = parts.slice(0, -1).join("\\");
  const elementPath = parts[parts.length - 1];

  return { rootPath, elementPath };
};

const updateTimespan = (timespan: string, frequency: number, frequencyType: string): string => {
  const parts = timespan.split(":").map(Number);
  switch (frequencyType.toLowerCase()) {
    case "seconds":
      parts[2] = frequency;
      break;
    case "minutes":
      parts[1] = frequency;
      break;
    case "hours":
      parts[0] = frequency;
      break;
    default:
      console.warn("Invalid frequencyType");
  }
  return parts.map((num) => num.toString().padStart(2, "0")).join(":");
};

const HierarchyView: React.FC<HierarchyViewProps> = ({
  formValuesForEdit,
  setFormValuesForEdit,
  selectedElements,
  tableDataSources,
  columns,
  isLoadingElements,
  showForm,
  activeStep,
  selectedRowKeys,
  setSelectedRowKeys,
  activeRecordsMetadata,
  setIsLoadingElements,
  setActiveStep,
  setShowForm,
  handleTableChange,
  handleGoToReqPage,
  tagPosition,
  pipoints,
  isProjectHierarchy,
  setTagPosition,
  isHandleSelectRunning = false,
  isRDBMSview = false,
  isAspenIp21View = false,
}) => {
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(20);
  const [ip21PageSize, setIp21PageSize] = useState<number>(10);
  const cluster = useSelector((store: RootState) => store.ClusterReducer.defaultClusterInfo);
  const requestJson = JSON.parse(formValuesForEdit?.requestJson || `{}`);
  const { rootPath, elementPath } = extractPaths(selectedElements?.path || "");
  const [isVisibleModal, setIsVisibleModal] = useState<boolean>(false);
  const { envVariables: Env } = useSelector((store: RootState) => store.AccountReducer);
  const intialValuesReqForm = { requestName: "", start_time_str: null, end_time_str: null, tags: selectedRowKeys.join(","), elementRootPath: rootPath, elementPath: elementPath, requestType: "raw", realTimeValues: 'current', frequency: "1", frequencyType: "seconds", period_ms: '5', refresh_period_ms: '3600000', all_descendants: requestJson?.all_descendants, output_format: "csv", activeTimezone: 'local', summary_interval_value: "1", summary_interval_unit: "d", summary_types: "", calculation_basis: "timeweighted", summary_time_type: "auto", page_size: "16384", chunking_num_tags: "1000", isAttributesManualEntry: false };
  const intialValuesReqFormIp21RDBMS = { requestName: "", columns: [] };
  const intialValuesReqFormIp21Aspen = { requestName: "", mode: "runOnce", parameters: [], parameterType: [], parameterValues: [] };
  const intialValuesSinkForm = { sinkKind: 'S3', sinkName: "", region: '', accessKeyId: '', secretAccessKey: '', database: '', mode: 'Append', schema: '', warehouse: '', role: '', databaseType: "None", tableName: '', landingEnabled: false, createNewTable: false };
  const enabledWorkspace = useSelector((store: RootState) => store.ClusterReducer.workspaceList)?.filter((item) => item.isEnabled)?.[0];
  const [intialValuesReqFormState, setIntialValuesReqFormState] = useState({} as any);
  const [intialValuesSinkFormState, setIntialValuesSinkFormState] = useState({} as any);
  const [intialValuesScheduleFormState, setIntialValuesScheduleFormState] = useState({} as any);
  const [selectedIp21Tab, setSelectedIp21Tab] = useState("simulation");
  const [tableRDBMSDataSources, setTableRDBMSDataSources] = useState([]);
  const [tableIp21DataSources, setTableIp21DataSources] = useState([]);
  const [queryLimit, setQueryLimit] = useState<number | null>(20);
  const intervalTimer = useRef<NodeJS.Timeout | null>(null);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [Ip21ErrMsg, setIp21ErrMsg] = useState("");

  // Generic formatter for dynamic RDBMS cells
  const formatRdbmsCell = (key: string, value: any) => {
    // Booleans -> TRUE/FALSE
    if (typeof value === 'boolean') {
      return value ? 'TRUE' : 'FALSE';
    }
    // Numbers: detect epoch seconds/milliseconds
    if (typeof value === 'number' && isFinite(value)) {
      // Heuristic: ms if >= 1e12 and <= 1e14; sec if between 1e9 and 1e11
      if (value >= 1e12 && value <= 1e14) {
        return moment(value).format('YYYY-MM-DD HH:mm:ss');
      }
      if (value >= 1e9 && value <= 1e11) {
        return moment(value * 1000).format('YYYY-MM-DD HH:mm:ss');
      }
      return value;
    }
    // Strings that look like ISO dates
    if (typeof value === 'string') {
      const isoLike = /\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}:\d{2}/.test(value);
      if (isoLike) {
        return moment(value).format('YYYY-MM-DD HH:mm:ss');
      }
      return value;
    }
    // Objects/arrays -> JSON string
    if (typeof value === 'object' && value !== null) {
      try { return JSON.stringify(value); } catch { return String(value); }
    }
    return value;
  };

  useEffect(() => {
    return () => {
      if (intervalTimer.current) {
        clearInterval(intervalTimer.current);
      }
    };
  }, []);

  // Dynamically derive RDBMS table columns from data keys
  const rdbmsColumns = useMemo(() => {
    if (!Array.isArray(tableRDBMSDataSources) || tableRDBMSDataSources.length === 0) return [] as any[];
    const sample = tableRDBMSDataSources[0] as Record<string, any>;
    return Object.keys(sample).map((k) => ({
      title: String(k).toUpperCase(),
      dataIndex: k,
      key: k,
      render: (val: any) => formatRdbmsCell(k, val),
    }));
  }, [tableRDBMSDataSources]);

  // Ensure each row has a stable key for Antd Table
  const rdbmsDataWithKeys = useMemo(() => {
    if (!Array.isArray(tableRDBMSDataSources)) return [] as any[];
    return tableRDBMSDataSources.map((row: any, idx: number) => ({
      key: row?.key ?? String(idx),
      ...row,
    }));
  }, [tableRDBMSDataSources]);


  const Ip21Columns = useMemo(() => {
    if (!Array.isArray(tableIp21DataSources) || tableIp21DataSources.length === 0) return [] as any[];
    const sample = tableIp21DataSources[0] as Record<string, any>;
    return Object.keys(sample).map((k) => ({
      title: String(k).toUpperCase(),
      dataIndex: k,
      key: k,
      render: (val: any) => formatRdbmsCell(k, val),
    }));
  }, [tableIp21DataSources]);


  const Ip21DataWithKeys = useMemo(() => {
    if (!Array.isArray(tableIp21DataSources)) return [] as any[];
    return tableIp21DataSources.map((row: any, idx: number) => ({
      key: row?.key ?? String(idx),
      ...row,
    }));
  }, [tableIp21DataSources]);

  const hierarchyTableDataSource = useMemo(() => {
    return tagPosition === "Elements"
      ? (tableDataSources || []).map((item: any) => ({
        key: item.attr_name,
        attr_name: item.attr_name || "",
        pipointName: item.pipoint_name || "",
        pipointType: item.pipointType || "",
        pipointClass: item.pipointClass || "",
        path: item.path || ""
      }))
      : (pipoints || []).map((item: any) => ({
        key: item.key,
        attr_name: item.pipointName || "",
        pipointName: item.pipointName || "",
        pipointType: item.pipointType || "",
        pipointClass: item.pipointClass || "",
        path: item.path || ""
      }));
  }, [tagPosition, tableDataSources, pipoints]);

  // Hide the built-in search filter icon/dropdown on columns when there is no data to filter
  const hierarchyTableColumns = useMemo(() => {
    if (hierarchyTableDataSource.length > 0) return columns;
    return (columns || []).map((col: any) => {
      const { filterDropdown, filterIcon, onFilter, ...rest } = col || {};
      return rest;
    });
  }, [columns, hierarchyTableDataSource]);

  const editorControllerProps = CustomMonacoEditor.useCustomMonacoEditorController({
    initialValue: ''
  });

  const editorControllerPropsIp21Aspen = CustomMonacoEditor.useCustomMonacoEditorController({
    initialValue: ""
  })

  const formatEditorQuery = () => {
    if (!(isRDBMSview || isAspenIp21View)) return;
    const current = editorControllerPropsIp21Aspen.editorValue;
    if (!current?.trim()) return current;
    try {
      const formatted = formatSql(current);
      if (formatted !== current) editorControllerPropsIp21Aspen.setEditorValue(formatted);
      return formatted;
    } catch {
      return current;
    }
  };

  const handleSubmitReqForm = (values: any) => {
    formatEditorQuery();
    setIntialValuesReqFormState(values);
    if (values?.requestType === "real_time" && !isRDBMSview && !isAspenIp21View) {
      setIntialValuesScheduleFormState({ ..._canvasdagFormInitialValues, scheduleTimingInfo: ScheduleTiming["Run Once"] });
    }
    if (isRDBMSview || isAspenIp21View) {
      setIntialValuesScheduleFormState({
        ..._canvasdagFormInitialValues,
        scheduleTimingInfo: (isAspenIp21View || isRDBMSview)
          ? (values.mode === "runOnce" ? ScheduleTiming["Run Once"] : ScheduleTiming["Recurring"])
          : ScheduleTiming["Run Once"]
      });
      
    }
    // Only update the active step if we're not already on the last step
    if (activeStep < 3) {
      setActiveStep(activeStep + 1);
    }
    // Ensure form remains visible when navigating between steps
    setShowForm(true);
  }

  const handleSubmitSinkForm = (values: any) => {
    setIntialValuesSinkFormState(values);
    // Only update the active step if we're not already on the last step
    if (activeStep < 3) {
      setActiveStep(activeStep + 1);
    }
    // Ensure form remains visible when navigating between steps
    setShowForm(true);
  }

  useEffect(() => {
    if (isEmpty(formValuesForEdit)) {
      setIntialValuesReqFormState((isRDBMSview || isAspenIp21View) ? ((isAspenIp21View || isRDBMSview) ? intialValuesReqFormIp21Aspen : intialValuesReqFormIp21RDBMS) : intialValuesReqForm);
      setIntialValuesSinkFormState(intialValuesSinkForm);
      setIntialValuesScheduleFormState(_canvasdagFormInitialValues);
      // Reset form state when there's no form values to edit
      setShowForm(false);
      setActiveStep(0);
    } else {
      // Always update the form when formValuesForEdit is not empty
      const shouldShowForm = !selectedRowKeys?.length && !isHandleSelectRunning;
      if (shouldShowForm) {
        setShowForm(true);
        setActiveStep(1);
      }
      if (isRDBMSview) {
        editorControllerPropsIp21Aspen.setEditorValue(requestJson?.query ?? "");
      } else if (isAspenIp21View) {
        editorControllerPropsIp21Aspen.setEditorValue(requestJson?.query_str ?? "");
      } else {
        editorControllerProps.setEditorValue(requestJson?.query_str ?? "");
      }
      // Use rootPath and elementPath when requestJson values are empty strings
      const effectiveRootPath = rootPath?.trim() ? rootPath : requestJson?.element_root;
      const effectiveElementPath = elementPath?.trim() ? elementPath : requestJson?.element_name;
      const timespanMatch = String(requestJson?.summary_interval_timespan || '').match(/^(\d+)([smhd])$/);


      const reqFromInitialValues = {
        requestName: formValuesForEdit?.modelRequestName,
        start_time_str: moment.utc(requestJson?.start_time_str).format('YYYY-MM-DD HH:mm:ssZ'),
        end_time_str: moment.utc(requestJson?.end_time_str).format('YYYY-MM-DD HH:mm:ssZ'),
        tags: selectedRowKeys?.toString() || formValuesForEdit?.tagSelections?.toString() || requestJson?.tags || "",
        elementRootPath: effectiveRootPath,
        elementPath: effectiveElementPath,
        all_descendants: requestJson?.all_descendants,
        requestType: formValuesForEdit?.requestType,
        realTimeValues: formValuesForEdit?.realTimeValues,
        frequency: formValuesForEdit?.frequency,
        frequencyType: formValuesForEdit?.frequencyType,
        period_ms: requestJson?.period_ms,
        output_format: requestJson?.output_format,
        refresh_period_ms: requestJson?.refresh_period_ms,
        activeTimezone: 'local',
        summary_interval_value: timespanMatch?.[1] || "1",
        summary_interval_unit: timespanMatch?.[2] || "d",
        summary_types: requestJson?.summary_types || "",
        calculation_basis: requestJson?.calculation_basis || "timeweighted",
        summary_time_type: requestJson?.summary_time_type || "auto",
        page_size: requestJson?.page_size || "",
        chunking_num_tags: requestJson?.chunking_num_tags || "",
        isAttributesManualEntry: !!(formValuesForEdit?.isAttributesManualEntry ?? requestJson?.isAttributesManualEntry)
      };
      const initialParamsFromReq: any[] = Array.isArray(formValuesForEdit?.requestControl?.initialParams) ? formValuesForEdit?.requestControl?.initialParams : [];
      const reconstructedParameterType: string[] = [];
      const reconstructedParameterValues: any[] = [];
      const reconstructedParameters: any[] = [];

      if (initialParamsFromReq.length > 0) {
        const groups: Record<string, any[]> = {};
        initialParamsFromReq.forEach((p: any) => {
          const match = String(p?.name || "").match(/^change_parameter(\d+)(?:_(start|end))?$/);
          if (match) {
            const idx = match[1];
            groups[idx] = groups[idx] || [];
            groups[idx].push(p);
          }
        });

        const indices = Object.keys(groups).map((k) => parseInt(k, 10)).sort((a, b) => a - b);
        indices.forEach((n, i) => {
          const arr = groups[String(n)] || [];
          const hasStart = arr.some((x: any) => /_start$/.test(String(x?.name || "")));
          const hasEnd = arr.some((x: any) => /_end$/.test(String(x?.name || "")));
          if (hasStart && hasEnd) {
            reconstructedParameterType[i] = 'pair';
            const start = arr.find((x: any) => /_start$/.test(String(x?.name || ""))) || {};
            const end = arr.find((x: any) => /_end$/.test(String(x?.name || ""))) || {};
            reconstructedParameterValues[i] = {
              start: { initial: start?.initialValue ?? '' },
              end: { initial: end?.initialValue ?? '' },
            };
            reconstructedParameters[i] = {
              id: `param-${i}`,
              type: 'pair',
              value: '',
              name: `change_parameter${i + 1}`,
              initialValue: '',
              incrementalValue: null
            };
          } else {
            reconstructedParameterType[i] = 'single';
            const single = arr[0] || {};
            reconstructedParameterValues[i] = {
              initial: single?.initialValue ?? '',
              increment: single?.incrementalValue ?? null
            };
            reconstructedParameters[i] = {
              id: `param-${i}`,
              type: 'single',
              value: '',
              name: `change_parameter${i + 1}`,
              initialValue: '',
              incrementalValue: null
            };
          }
        });
      }

      const reqFromInitialValuesip21Rdbms = {
        ...reqFromInitialValues,
        requestName: formValuesForEdit?.modelRequestName,
        columns: formValuesForEdit?.changeColumns,
        query: requestJson?.query,
        mode: formValuesForEdit?.mode || "runOnce",
        parameterType: reconstructedParameterType,
        parameterValues: reconstructedParameterValues,
        parameters: reconstructedParameters,
      };
      const reqFromInitialValuesip21Aspen = {
        ...reqFromInitialValues,
        requestName: formValuesForEdit?.modelRequestName,
        mode: formValuesForEdit?.mode || "runOnce",
        parameterType: reconstructedParameterType,
        parameterValues: reconstructedParameterValues,
        parameters: reconstructedParameters,
        query:requestJson?.query_str
      };
      setIntialValuesReqFormState((isRDBMSview || isAspenIp21View) ? (isAspenIp21View ? reqFromInitialValuesip21Aspen : reqFromInitialValuesip21Rdbms) : reqFromInitialValues);
      setIntialValuesSinkFormState({
        sinkKind: formValuesForEdit?.sinkDetails?.sinkKind,
        sinkName: formValuesForEdit?.sinkDetails?.sinkName,
        databaseType: formValuesForEdit?.sinkDetails?.databaseType,
        landingConnectionName: formValuesForEdit?.sinkDetails?.landingConnectionName,
        tableName: formValuesForEdit?.sinkDetails?.tableName,
        landingEnabled: formValuesForEdit?.sinkDetails?.landingEnabled,
        fsType: formValuesForEdit?.sinkDetails?.fsType,
        connectionNameForExternalStaging: formValuesForEdit?.sinkDetails?.connectionNameForExternalStaging,
        externalStagingLocation: formValuesForEdit?.sinkDetails?.externalStagingLocation,
        createNewTable: formValuesForEdit?.sinkDetails?.createNewTable,
        schema: formValuesForEdit?.sinkDetails?.schema,
        warehouse: formValuesForEdit?.sinkDetails?.warehouse,
        userRole: formValuesForEdit?.sinkDetails?.userRole,
        mode: formValuesForEdit?.sinkDetails?.mode
      });
      setIntialValuesScheduleFormState({
        ..._canvasdagFormInitialValues,
        ...(formValuesForEdit?.dagGenDto || {}),
        scheduleTimingInfo: formValuesForEdit?.dagGenDto?.schedule_interval === "@once"
          ? ScheduleTiming["Run Once"]
          : ScheduleTiming["Recurring"]
      });
    }
  }, [formValuesForEdit, elementPath, rootPath]);

  const handleSubmitScheduleForm = (values: any) => {
    setIntialValuesScheduleFormState(values);
    handleOnPublish(values);
  };

  const handleQuerySubmit = () => {
    setIsVisibleModal(false);
  };

  const handleQueryRun = () => {
    setTableRDBMSDataSources([]);
    const formattedQuery = formatEditorQuery() ?? editorControllerPropsIp21Aspen?.editorValue;
    setTableIp21DataSources([]);
    if (intervalTimer.current) {
      clearInterval(intervalTimer.current);
      intervalTimer.current = null;
    }
    setIp21ErrMsg("");
    setIsLoadingData(true);
    const jsonDataForDBRelational = {
      nodeName: activeRecordsMetadata?.nodeName,
      json: JSON.stringify({
        command: "get-meta-data",
        node_name: activeRecordsMetadata?.nodeName,
        query: formattedQuery,
        mode:"simulation",
        limit:queryLimit,
        max_retries:1, 
        rdbms_type: activeRecordsMetadata?.managedNodeInfo?.connectionDetails?.rdbmsType?.data,
        dbname:activeRecordsMetadata?.afStructureDto?.databaseName,
      }),
      state: "new",
      requestName: `list_databases_simulation_run_${uuid()}`,
      sinkKind: "S3",
      nodeKind: activeRecordsMetadata?.sourceType,
      connectionName: activeRecordsMetadata?.connectionName,
    };
    const jsonDataForDBIp21 = {
      nodeName: activeRecordsMetadata?.nodeName,
      json: JSON.stringify({
        command: "get_query_data",
        // ip21_host:  activeRecordsMetadata?.managedNodeInfo?.ip21Host,
        // ip21_port: Number(activeRecordsMetadata?.managedNodeInfo?.ip21Port),
        // ip21_user: activeRecordsMetadata?.managedNodeInfo?.ip21User,
        // ip21_dbname: activeRecordsMetadata?.managedNodeInfo?.ip21DbName,
        // ip21_password: activeRecordsMetadata?.managedNodeInfo?.ip21Password,
        ip21_connection_id: activeRecordsMetadata?.ip21ConnectionId,
        ip21_dbname: activeRecordsMetadata?.ip21DbName,
        node_name: activeRecordsMetadata?.nodeName,
        // query_str: isRDBMSview ? editorControllerProps?.editorValue : editorControllerPropsIp21Aspen?.editorValue,
        query_str: formattedQuery,
        mode:selectedIp21Tab,
        limit:queryLimit,
        max_retries: 1
      }),
      state: "new",
      requestName: `list_databases_simulation_run_${uuid()}`,
      sinkKind: "S3",
      nodeKind: activeRecordsMetadata?.sourceType,
      connectionName: activeRecordsMetadata?.connectionName,
    };

    DataSourceHandler.CreateHierarchyRequest(
      `${Env?.REACT_APP_PLATFORM_URL}/databrowser/api`,
      isRDBMSview ? jsonDataForDBRelational : jsonDataForDBIp21,
      (response: any) => {
        intervalTimer.current = setInterval(() => pollRdbmsDatabaseData(response?.data?.id), 4000);
      },
      (error: any) => {
        errorHandlerFor400(error);
        setIsLoadingData?.(false);
      }
    );
  };

  const pollRdbmsDatabaseData = (reqID: string) => {
    DataSourceHandler.GetViewReqJson(
      `${Env?.REACT_APP_PLATFORM_URL}/databrowser/api`,
      reqID,
      (res) => {
        if (res?.state === "SUCCESS" || res?.state === "STAGED") {
          if (intervalTimer.current) {
            clearInterval(intervalTimer.current);
            intervalTimer.current = null;
          }
          setTableRDBMSDataSources(res.data);
          setTableIp21DataSources(res.data);
          setIsLoadingData?.(false);
        }
        if (res?.state === "FAILED") {
          if (intervalTimer.current) {
            clearInterval(intervalTimer.current);
            intervalTimer.current = null;
          }
          setIsLoadingData?.(false);
          setIp21ErrMsg(res?.error?.toString() as any);
          setTableRDBMSDataSources([]);
          setTableIp21DataSources([]);
        }
      }
    );
  };


  const handleOnPublish = (values: any) => {
    const commandReqTypeRef = {
      "raw": "get_historical_data",
      "real_time": "get_current_data",
      "interpolated": "get_interpolated_data",
      "summary": "get_summary"
    }
    setIsLoadingElements(true);
    if (!(!isEmpty(enabledWorkspace) && enabledWorkspace?.isEnabled && cluster && cluster.defaultCluster)) {
      errorAlert("Cluster should be the default cluster and workspace should be enabled");
      setIsLoadingElements(false);
      return;
    }
    const get_command = intialValuesReqFormState?.requestType === "real_time" ? (intialValuesReqFormState?.realTimeValues === "live" ? "get_live_data" : "get_current_data") : commandReqTypeRef[intialValuesReqFormState?.requestType as keyof typeof commandReqTypeRef];
    const initialParams: any[] = [];
    if (isAspenIp21View || isRDBMSview) {
      const paramTypes = intialValuesReqFormState?.parameterType || [];
      const paramValues = intialValuesReqFormState?.parameterValues || [];
      const params = intialValuesReqFormState?.parameters || [];

      params.forEach((param: any, index: number) => {

        const type = paramTypes?.[index] || 'single';
        if (type === 'pair') {
          const startInit = paramValues?.[index]?.start?.initial ?? '';
          const endInit = paramValues?.[index]?.end?.initial ?? '';
          initialParams.push({
            name: `change_parameter${index + 1}_start`,
            type: 'pair',
            initialValue: startInit
          });
          initialParams.push({
            name: `change_parameter${index + 1}_end`,
            type: 'pair',
            initialValue: endInit
          });
        } else {
          const init = paramValues?.[index]?.initial ?? '';
          const inc = paramValues?.[index]?.increment ?? '';
          initialParams.push({
            name: `change_parameter${index + 1}`,
            type: 'single',
            initialValue: init,
            incrementalValue: inc
          });
        }
      });
    }
    const payload = {
      serverName: activeRecordsMetadata?.afStructureDto?.serverName,
      dataBaseName: activeRecordsMetadata?.afStructureDto?.databaseName,
      nodeKind: activeRecordsMetadata?.managedNodeInfo?.nodeKind,
      connectionName: activeRecordsMetadata?.connectionName || "",
      ...(isRDBMSview && Env && { rdbmsIntegratorWorkflowIdOne: Env?.RDBMS_INTEGRATOR_WORKFLOW_ID_ONE }),
      ...(isRDBMSview && Env && { rdbmsIntegratorWorkflowIdTwo: Env?.RDBMS_INTEGRATOR_WORKFLOW_ID_TWO }),
      ...(isAspenIp21View && Env && { ip21IntegratorWorkflowIdOne: Env?.IP21_INTEGRATOR_WORKFLOW_ID_ONE }),
      ...(isAspenIp21View && Env && { ip21IntegratorWorkflowIdTwo: Env?.IP21_INTEGRATOR_WORKFLOW_ID_TWO }),
      ...(!isRDBMSview && !isAspenIp21View && Env && { piIntegratorWorkflowIdOne: Env?.PI_INTEGRATOR_WORKFLOW_ID_ONE }),
      ...(!isRDBMSview && !isAspenIp21View && Env && { piIntegratorWorkflowIdTwo: Env?.PI_INTEGRATOR_WORKFLOW_ID_TWO }),
      // ...(isRDBMSview && { query: editorControllerProps?.editorValue, querylimit: queryLimit }),
      ...((isAspenIp21View||isRDBMSview) && { query: editorControllerPropsIp21Aspen?.editorValue, querylimit: queryLimit }),
      // ...(isRDBMSview && { changeColumns: intialValuesReqFormState?.columns, }),
      ...((isAspenIp21View||isRDBMSview) && { mode: intialValuesReqFormState?.mode, requestControl:{initialParams:intialValuesReqFormState?.mode === "runOnce" ? [] : initialParams} }),
      tagSelections: intialValuesReqFormState?.tags
        ? intialValuesReqFormState.tags.split(',').map((tag: string) => tag.trim()).filter(Boolean)
        : selectedRowKeys,
      isAttributesManualEntry: !!intialValuesReqFormState?.isAttributesManualEntry,
      requestType: intialValuesReqFormState?.requestType,
      realTimeValues: intialValuesReqFormState?.realTimeValues || "current",
      frequency: intialValuesReqFormState?.frequency,
      frequencyType: intialValuesReqFormState?.frequencyType,
      period_ms: intialValuesReqFormState?.period_ms,
      refresh_period_ms: intialValuesReqFormState?.refresh_period_ms,
      triggerImmediately: false,
      tagSelectedType: tagPosition,
      requestJson: JSON.stringify({
        // command: isAspenIp21View ? "get_query_data" : get_command,
        ...(!isRDBMSview && { command: isAspenIp21View ? "get_query_data" : get_command}),
        node_name: `${activeRecordsMetadata?.managedNodeInfo?.nodeName}`,
        ...(isRDBMSview && { query: editorControllerPropsIp21Aspen?.editorValue }),
        ...((isAspenIp21View) && {
          query_str: editorControllerPropsIp21Aspen?.editorValue, 
          // ip21_host:  activeRecordsMetadata?.managedNodeInfo?.ip21Host,
          // ip21_port: Number(activeRecordsMetadata?.managedNodeInfo?.ip21Port),
          // ip21_user: activeRecordsMetadata?.managedNodeInfo?.ip21User,
          // ip21_dbname: activeRecordsMetadata?.managedNodeInfo?.ip21DbName,
          // ip21_password: activeRecordsMetadata?.managedNodeInfo?.ip21Password,
          ip21_connection_id: activeRecordsMetadata?.ip21ConnectionId,
          ip21_dbname: activeRecordsMetadata?.ip21DbName,
          max_retries: 1,
          }),
          ...(!isAspenIp21View && !isRDBMSview && { tags: intialValuesReqFormState?.tags,  fullpath: true, server_type: activeRecordsMetadata?.afStructureDto?.serverType ?? "PI_SERVER",
            server_name: activeRecordsMetadata?.afStructureDto?.serverName,
            db_name: (activeRecordsMetadata?.afStructureDto?.serverType ?? "PI_SERVER") === "PI_SERVER" ? "" : activeRecordsMetadata?.afStructureDto?.databaseName,
            ...(tagPosition === "Elements" && {
              element_name: intialValuesReqFormState?.elementPath,
              element_root: intialValuesReqFormState?.elementRootPath,
            }),
            all_descendants: intialValuesReqFormState?.all_descendants,
            ...(intialValuesReqFormState?.requestType === "interpolated" && {
              timespan: updateTimespan(
                "00:00:00",
                parseInt(intialValuesReqFormState?.frequency),
                intialValuesReqFormState?.frequencyType
              ),
            }),  ...(intialValuesReqFormState?.requestType !== "real_time" && {
              start_time_str: (intialValuesReqFormState?.activeTimezone === "utc"
                ? moment.utc(intialValuesReqFormState?.start_time_str)
                : moment(intialValuesReqFormState?.start_time_str).local()
              ).format("YYYY-MM-DD HH:mm:ssZ"),
              end_time_str: (intialValuesReqFormState?.activeTimezone === "utc"
                ? moment.utc(intialValuesReqFormState?.end_time_str)
                : moment(intialValuesReqFormState?.end_time_str).local()
              ).format("YYYY-MM-DD HH:mm:ssZ"),
            }),
            ...(intialValuesReqFormState?.requestType === "real_time" &&
              intialValuesReqFormState?.realTimeValues === "live" && {
              period_ms: Number(intialValuesReqFormState?.period_ms),
              // realTimeValues: intialValuesReqFormState?.realTimeValues,
              refresh_period_ms: Number(intialValuesReqFormState?.refresh_period_ms),
            }),
            ...(intialValuesReqFormState?.requestType === "summary" && {
              summary_types: intialValuesReqFormState?.summary_types,
              calculation_basis: intialValuesReqFormState?.calculation_basis,
              summary_time_type: intialValuesReqFormState?.summary_time_type,
              ...(intialValuesReqFormState?.summary_interval_value && {
                summary_interval_timespan: `${intialValuesReqFormState.summary_interval_value}${intialValuesReqFormState.summary_interval_unit || 'd'}`,
              }),
              ...(intialValuesReqFormState?.page_size && {
                page_size: Number(intialValuesReqFormState.page_size),
              }),
            }),
            ...(intialValuesReqFormState?.requestType !== "real_time" && intialValuesReqFormState?.chunking_num_tags && {
              chunking_num_tags: Number(intialValuesReqFormState.chunking_num_tags),
            }),
            output_format: "csv" }),
      }),
      nodeName: activeRecordsMetadata?.managedNodeInfo?.nodeName,
      dagGenDto: {
        dag_id: intialValuesReqFormState?.requestName + "-DAG1",
        description: values?.description || "",
        schedule_interval: values.scheduleTimingInfo === ScheduleTiming.None
          ? 'None'
          : (values.scheduleTimingInfo === ScheduleTiming.Recurring
            ? values.cronExpression
            : "@once"),
        start_date: values.scheduleTimingInfo === ScheduleTiming.Recurring ? values.startDate : values.scheduleTimingInfo === ScheduleTiming['Run Once'] ? values.formattedRunOnceExecutionDate : formatScheduleStartDate(moment().utc()),
        isDatabricks: Env?.REACT_APP_DATABRICKS,
        scheduleType: values?.scheduleType,
        default_args: {
          owner: "Airflow",
          depends_on_past: false,
          email: values?.overrideNotificationEmail ? [values?.notificationEmails] : [],
          email_on_failure: values?.overrideNotificationEmail,
          email_on_retry: false,
          retries: values?.scheduleAdvancedMode ? parseInt(values.retries as unknown as string) : 0,
          retry_delay: values?.scheduleAdvancedMode ? parseInt(values.retryDelay as unknown as string) : 0,
          execution_timeout: parseInt(values.execution_timeout as unknown as string) *
            (executionTimeFrequencyMultiplier[
              values.execution_timeout_frequency as keyof typeof executionTimeFrequencyMultiplier
            ] || 1)
        },
        nodes: [
          {
            id: "0099f361-f877-4600-85f4-58b7445bc143",
            fn_id: "DatabricksSubmitRunOperator",
            fnName: "DatabricksSubmitRunOperator",
            title: "Databricks Operator",
            inputPorts: [],
            outputPorts: [
              {
                id: "4eb20487-942d-48b9-aea0-7b32d94c58c4",
                type: "OUT-0",
                link: [],
                parentNode: "0099f361-f877-4600-85f4-58b7445bc143"
              }
            ],
            data: [
              { key: "task_id", value: "task_id_" + uuid(), arg_index: 1, variable_type: "string", useKey: true },
              { key: "existing_workflow", value: true, arg_index: 6, variable_type: "boolean", useKey: true },
              { key: "workflow_id", value: Env?.REACT_APP_PI_INTEGRATOR_WORKFLOW_ID, arg_index: 2, variable_type: "integer", useKey: true },
              { key: "databricks_conn_id", value: enabledWorkspace?.workspaceName, arg_index: 3, variable_type: "string", useKey: true },
              { key: "notebook_task", value: "ssh_spark", arg_index: 4, variable_type: "string", useKey: true },
              { key: "existing_cluster_id", value: cluster?.databricksClusterId, arg_index: 5, variable_type: "string", useKey: true },
              { key: "base_parameters", value: "", arg_index: 8, variable_type: "string", useKey: true },
              { key: "databrick_file_path", value: "", arg_index: 7, variable_type: "string", useKey: true }
            ],
            type: "component",
            df_name: "databricks_submit_run_operator_1",
            previewAllowed: true,
            field_workflow_config_mapping: {},
            field_var_mapping: [],
            extraData: {}
          }
        ],
        links: [],
        workspaceType: enabledWorkspace?.workspaceType,
        max_active_runs: values?.max_active_runs,
        catchup: false,
        exponential_backoff: false
      },
      sinkDetails: {
        sinkKind: intialValuesSinkFormState?.sinkKind,
        sinkName: intialValuesSinkFormState?.sinkName,
        databaseType: intialValuesSinkFormState?.databaseType,
        tableName: intialValuesSinkFormState?.tableName,
        landingEnabled: intialValuesSinkFormState?.landingEnabled,
        landingConnectionName: intialValuesSinkFormState?.landingConnectionName,
        fsType: intialValuesSinkFormState?.fsType,
        connectionNameForExternalStaging: intialValuesSinkFormState?.connectionNameForExternalStaging,
        externalStagingLocation: intialValuesSinkFormState?.externalStagingLocation,
        createNewTable: intialValuesSinkFormState?.createNewTable,
        schema: intialValuesSinkFormState?.schema,
        warehouse: intialValuesSinkFormState?.warehouse,
        userRole: intialValuesSinkFormState?.userRole,
        mode: intialValuesSinkFormState?.mode
      },
      modelRequestName: intialValuesReqFormState?.requestName,
    };
    if (isEmpty(formValuesForEdit)) {
      DataSourceHandler.PublishHierarchyRequest(
        `${Env?.REACT_APP_PLATFORM_URL}/databrowser/api`,
        payload,
        (response: any) => {
          setIsLoadingElements(false);
          handleGoToReqPage?.();
          setSelectedRowKeys([]);
          setFormValuesForEdit?.(null);
          successAlert("Request has been successfully submitted");
        },
        (error: any) => {
          setIsLoadingElements(false);
          errorHandlerFor400(error);
          // setSelectedRowKeys([]);
          // setFormValuesForEdit?.(null);
        }
      );
    } else {
      DataSourceHandler.UpdateModelRequest(
        `${Env?.REACT_APP_PLATFORM_URL}/databrowser/api`,
        String(formValuesForEdit?.id),
        payload,
        (response: any) => {
          setIsLoadingElements(false);
          handleGoToReqPage?.();
          setFormValuesForEdit?.(null);
          successAlert("Request has been successfully updated");
        },
        (error: any) => {
          setIsLoadingElements(false);
          errorHandlerFor400(error);
          // setFormValuesForEdit?.(null);
        }
      );
    }

  }

  return (
    <Flex className={styles.boxHierarchyView} flexDirection="column" gap="0px">
      <ShowWhenTrue show={(!isRDBMSview && !isAspenIp21View) || ((isRDBMSview || isAspenIp21View) && activeStep > 0)}>
        <div className={styles.labelHierarchyView}>
          <Flex gap="8px" alignItems={"center"}>
            <FaFolder />
            <Text fontSize="14px" fontWeight={600}>{formTitle[activeStep]}</Text>
            <ShowWhenTrue show={!isEmpty(intialValuesReqFormState?.requestName)}>
              <Text fontSize="14px" fontWeight={400}>
                {`Pipeline Name: ${intialValuesReqFormState.requestName}`}
              </Text>
            </ShowWhenTrue>
          </Flex>
          <ShowWhenTrue show={activeStep === 0 && !isProjectHierarchy && !isRDBMSview && !isAspenIp21View}>
            <Flex gap="10px" justifyContent={"end"} alignItems={"center"}>
              <ShowWhenTrue show={!isEmpty(activeRecordsMetadata?.afStructureDto?.serverName) && !isEmpty(activeRecordsMetadata?.afStructureDto?.databaseName) && activeRecordsMetadata?.afStructureDto?.serverType === "PI_AF"}>
                <Radio.Group className={"custom-radio-group-tag-position"} value={tagPosition} onChange={(e) => {
                  if (isEmpty(formValuesForEdit)) {
                    setSelectedRowKeys([]);
                  }
                  setTagPosition(e.target.value)
                }} style={{ display: 'flex' }}>
                  <Radio.Button disabled={!isEmpty(formValuesForEdit)} value="Elements">Attributes</Radio.Button>
                  {Env?.HIDE_PIPOINTS && <Radio.Button disabled={!isEmpty(formValuesForEdit)} value="Pipoint">PIPoint</Radio.Button>}
                </Radio.Group>
              </ShowWhenTrue>
              <ShowWhenTrue show={!isEmpty(activeRecordsMetadata?.afStructureDto?.serverName) && activeRecordsMetadata?.afStructureDto?.serverType === "PI_SERVER"}>
                <Radio.Group className={"custom-radio-group-tag-position"} value={tagPosition} onChange={(e) => {
                  if (isEmpty(formValuesForEdit)) {
                    setSelectedRowKeys([]);
                  }
                  setTagPosition(e.target.value)
                }} style={{ display: 'flex' }}>
                  <Radio.Button disabled={!isEmpty(formValuesForEdit)} value="Pipoint">PIPoint</Radio.Button>
                </Radio.Group>
              </ShowWhenTrue>
            </Flex>
          </ShowWhenTrue>

        </div>
      </ShowWhenTrue>
      <ShowWhenTrue show={(isRDBMSview || isAspenIp21View) && activeStep === 0}>
        <div className={styles.queryToolControls}>
          <Flex gap="8px" alignItems={"center"}>
            <div >
              <Radio.Group size="large" className={"custom-radio-group-tag-position"} value={selectedIp21Tab} onChange={(e) => {
                if (isEmpty(formValuesForEdit)) {
                  setSelectedRowKeys([]);
                }
                setTagPosition(e.target.value)
              }} style={{ display: 'flex' }}>
                <Radio.Button disabled={true} value="browse">Browse</Radio.Button>
                <Radio.Button value="simulation">Simulation</Radio.Button>
              </Radio.Group>
            </div>
          </Flex>
          <Flex gap="8px" alignItems={"center"}>
            <Text fontSize="16px" fontWeight={400} color={"#98a1b3"}>Limit</Text>
            <input
              type="number"
              value={queryLimit ?? ''}
              onChange={(e) => {
                const value = e.target.value === '' ? null : Number(e.target.value);
                setQueryLimit(value);
              }}
              min="1"
              max="1000"
            />
            <Button
              className={styles.runButton}
              disabled={isLoadingData}
              loading={isLoadingData}
              onClick={handleQueryRun}
            >
              <FaPlay /> Run
            </Button>
          </Flex>
        </div>
      </ShowWhenTrue>
      <Modal
        title={"Query"}
        visible={isVisibleModal}
        width={'80vw'}
        onOk={handleQuerySubmit}
        okText="Apply"
        onCancel={() => setIsVisibleModal(false)}
        bodyStyle={{ height: '650px', overflow: 'auto', paddingBottom: '5px' }}
        okButtonProps={{
          style: {
            backgroundColor: '#fcad01',
            boxShadow: '0 1px 4px 0 rgba(252, 173, 1, 0.72)',
            color: '#262231'
          }
        }}
        cancelButtonProps={{
          type: 'link',
          style: {
            color: 'white'
          }
        }}
        className="editorTab"
      >
        <CustomMonacoEditor
          {...((isAspenIp21View || isRDBMSview) ? editorControllerPropsIp21Aspen : editorControllerProps)}
          editorHeight="590px"
          editorWidth="100%"
          scrollBeyondLastLine
          toolbarOptions={[
            "Copy", "Clear", "Format"
          ]}
          language="sql"
        />
      </Modal>
      <div className={styles.hierarchyMainContent}>
        <ShowWhenTrue show={(isRDBMSview || isAspenIp21View) && activeStep === 0}>
          <div className={styles.queryInput}>
            <CustomMonacoEditor
              {...((isAspenIp21View || isRDBMSview) ? editorControllerPropsIp21Aspen : editorControllerProps)}
              editorHeight="220px"
              editorWidth="100%"
              scrollBeyondLastLine
              language="sql"
              // setShowModal={(value, show)=>setShowQueryModal?.(value, show)}
              setShowModal={(value, show) => { editorControllerPropsIp21Aspen.setEditorValue(value); setIsVisibleModal(show); }}
            />
          </div>
        </ShowWhenTrue>
        <ShowWhenTrue show={!showForm && (isRDBMSview || isAspenIp21View)}>
          <ShowWhenTrue show={!isEmpty(Ip21ErrMsg) && !isLoadingData}>
            <Flex justifyContent={"center"} alignItems={"center"} marginTop={"40px"}>
              <WarningIcon size={32} />
              <Text fontSize="16px" fontWeight={400} color={"#ff7766"}>{Ip21ErrMsg}</Text>
            </Flex>
          </ShowWhenTrue>
          <ShowWhenTrue show={isLoadingData}>
            <Flex justifyContent={"center"} alignItems={"center"} marginTop={"40px"}>
              <InPageSpinner /> &nbsp;
              <Text fontSize="16px" fontWeight={400} color={"#ff7766"}>Wait, Fetching Data...</Text>
            </Flex>
          </ShowWhenTrue>
          <ShowWhenTrue show={(isAspenIp21View || isRDBMSview) && isEmpty(Ip21ErrMsg) && !isLoadingData}>
            <Table
              columns={Ip21Columns}
              dataSource={Ip21DataWithKeys}
              size="small"
              className={styles.dataTableHierarchyIp21}
              pagination={{
                current: currentPage,
                pageSize: ip21PageSize,
                total: Ip21DataWithKeys?.length || 0,
                onChange: (page, pageSize) => {
                  setCurrentPage(page);
                  if (pageSize && pageSize !== ip21PageSize) {
                    setIp21PageSize(pageSize);
                  }
                },
                showSizeChanger: true,
                onShowSizeChange: (_current, size) => {
                  setIp21PageSize(size);
                  setCurrentPage(1);
                },
                pageSizeOptions: (() => {
                  const total = Ip21DataWithKeys?.length || 0;
                  const base = ['10', '20', '50', '100', '250'];
                  const allowed = base.filter(opt => Number(opt) <= total);
                  const set = new Set<string>(allowed.length ? allowed : ['10']);
                  set.add(String(ip21PageSize));
                  return Array.from(set).sort((a, b) => Number(a) - Number(b));
                })(),
                className: styles.tablePagination,
                showTotal: (total) => `Total ${total} items`
              }}
              locale={{
                emptyText: (
                  <Text color="white" fontSize={14}>
                    No Data Available
                  </Text>
                ),
              }}
            />
          </ShowWhenTrue>
          {/* <ShowWhenTrue show={isRDBMSview && isEmpty(Ip21ErrMsg) && !isLoadingData}>
            <Table
              columns={rdbmsColumns}
              dataSource={rdbmsDataWithKeys}
              size="small"
              className={styles.dataTableHierarchyIp21}
              pagination={{
                current: currentPage,
                pageSize: ip21PageSize,
                total: rdbmsDataWithKeys?.length || 0,
                onChange: (page) => setCurrentPage(page),
                showSizeChanger: true,
                pageSizeOptions: ['20', '50', '100', '250'],
                className: styles.tablePagination,
                showTotal: (total) => `Total ${total} items`
              }}
              locale={{
                emptyText: (
                  <Text color="white" fontSize={14}>
                    No Data Available
                  </Text>
                ),
              }}
            />
          </ShowWhenTrue> */}
        </ShowWhenTrue>
        <ShowWhenTrue show={!showForm && !isRDBMSview && !isAspenIp21View}>
          <Table
            dataSource={hierarchyTableDataSource}
            onChange={(pagination, filters, sorter, extra) => {
              handleTableChange as any
              setCurrentPage(pagination.current || 1);
              setPageSize(pagination.pageSize || 20);
            }}
            loading={isLoadingElements}
            columns={hierarchyTableColumns}
            size="small"
            className={isProjectHierarchy ? styles.projectDataTableHierarchy : styles.dataTableHierarchy}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: tagPosition === "Elements" ? tableDataSources?.length || 0 : pipoints?.length || 0,
              onChange: (page) => setCurrentPage(page),
              showSizeChanger: true,
              pageSizeOptions: ['20', '50', '100', '250'],
              className: styles.tablePagination,
              showTotal: (total) => `Total ${total} items`
            }}
            key={tagPosition}
            rowKey={'key'}
            locale={{
              emptyText: (
                <Text color="white" fontSize={14}>
                  {!isEmpty(selectedElements)
                    ? `No ${tagPosition === "Elements" ? 'attributes' : 'PIPoints'} found for the selected element.`
                    : 'Please select an element from the hierarchy (on the left) to browse through attributes.'}
                </Text>
              ),
              filterTitle: tagPosition === "Elements" ? "Search Attribute Names" : "Search PIPoint Names",
            }}
          />
        </ShowWhenTrue>
        <ShowWhenTrue show={showForm && activeStep > 0}>
          <ShowWhenTrue show={activeStep === 1}>
            <HierarchyReqForm
              activeStep={activeStep}
              setActiveStep={setActiveStep}
              initialValues={intialValuesReqFormState}
              tagPosition={tagPosition}
              selectedRowKeys={useMemo(() => {
                return selectedRowKeys.length > 0
                  ? selectedRowKeys
                  : formValuesForEdit?.tagSelections || [];
              }, [formValuesForEdit?.tagSelections, selectedRowKeys])}
              setShowForm={setShowForm}
              handleOnSubmit={handleSubmitReqForm}
              editorControllerPropsIp21Aspen={editorControllerPropsIp21Aspen}
              formSchema={(isRDBMSview || isAspenIp21View) ? ((isAspenIp21View || isRDBMSview) ? formSchemaReqFormIp21Aspen : formSchemaReqFormRDBMS) : formSchemaReqForm}
              isRDBMSview={isRDBMSview}
              isAspenIp21View={isAspenIp21View}
              isEditMode={!!formValuesForEdit} />
          </ShowWhenTrue>
          <ShowWhenTrue show={activeStep === 2}>
            <HierarchySinkForm
              activeStep={activeStep}
              setActiveStep={setActiveStep}
              initialValues={intialValuesSinkFormState}
              formSchema={formSchemaSinkForm}
              isRDBMSview={isRDBMSview}
              isAspenIp21View={isAspenIp21View}
              handleOnSubmit={handleSubmitSinkForm} />
          </ShowWhenTrue>
          <ShowWhenTrue show={activeStep === 3}>
            <HierarchyScheduleForm
              activeStep={activeStep}
              setActiveStep={setActiveStep}
              intialValuesScheduleFormState={intialValuesScheduleFormState}
              handleOnSubmit={handleSubmitScheduleForm}
              isLoadingElements={isLoadingElements}
              isEditForm={!isEmpty(formValuesForEdit) ? true : false}
              isDisableRequrring={
                (isRDBMSview || isAspenIp21View)
                  ? intialValuesReqFormState?.mode === "runOnce"
                  : intialValuesReqFormState?.requestType === "real_time" 
                  && intialValuesReqFormState?.realTimeValues === "live"
              }
              
              isDisableRunOnce={
                (isAspenIp21View || isRDBMSview)
                  ? intialValuesReqFormState?.mode === "recurring"
                  : false
              }
              // isDisableRequrring={isRDBMSview ? false : (isAspenIp21View ? intialValuesReqFormState?.mode === "runOnce" : intialValuesReqFormState?.requestType === "real_time" ? true : false)}
              // isDisableRunOnce={!isAspenIp21View ? false : intialValuesReqFormState?.mode === "recurring"}

              selectedScheduleTimingInfo={formValuesForEdit?.dagGenDto?.schedule_interval || ""}
            />
          </ShowWhenTrue>
        </ShowWhenTrue>
      </div>
      <ShowWhenTrue show={(!isProjectHierarchy && activeStep == 0)}>
        <div className={styles.hierarchyTableFooter}>
          <button
            className="btn btn-yellow btn-md"
            onClick={() => {
              // if(!isEmpty(selectedRowKeys)){
              formatEditorQuery();
              setShowForm(true);
              setActiveStep(activeStep + 1);
              // }else{
              //   errorAlert("Please select the attributes to proceed");
              // }
            }}
          >
            Next
          </button>
        </div>
      </ShowWhenTrue>
    </Flex>
  );
};

export default HierarchyView;
